import { useHookstate } from "@hookstate/core";
import {
    alpha,
    Box,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { isProfileClickedState } from "../hooks/store";
import theme from "../theme/Theme";
import { Logo } from "../assets";
import { IoClose } from "react-icons/io5"
import { createUpdateProfile, fetchProfile } from "../api/ApiService";
import { toast } from "react-toastify";
import { IoIosImages } from "react-icons/io";
import imageCompression from "browser-image-compression";
import { FaPencilAlt } from "react-icons/fa";
import CustomButton from "./CustomButton";

const ProfileDialog = () => {
    const [binaryImage, setBinaryImage] = useState(null);
    const [isCheck, setIsCheck] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [buttonTitle, setButtonTitle] = useState("");
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState(null);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(null);
    const [imageLoader, setImageLoader] = useState(false);
    const profileHookState = useHookstate(isProfileClickedState);
    const MAX_FILE_SIZE_MB = 0.4;
    const MAX_HEIGHT_WIDTH = 300;

    const getUserProfile = async () => {
        const response = await fetchProfile();
        if (response && response.status === 404) {
            setIsEdit(true);
            setButtonTitle("Save");
        } else {
            if (response.data) {
                setName(response.data.fullName);
                setEmail(response.data.emailAddress);
                setIsCheck(response.data.consentToDisplayWinnings);
                setBinaryImage(response.data.profilePhoto);
            }

            setButtonTitle("Update");
        }
    };

    useEffect(() => {
        getUserProfile();
    }, []);

    const onDrop = (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
            try {
                setImageLoader(true);
                imageCompression(file, {
                    maxSizeMB: MAX_FILE_SIZE_MB,
                    maxWidthOrHeight: MAX_HEIGHT_WIDTH,
                    useWebWorker: true,
                }).then((compressedFile) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setBinaryImage(reader.result);
                    };
                    reader.readAsDataURL(file);
                    setImageLoader(false);
                });
            } catch (error) {
                setImageLoader(false);
                console.error("Image compression error:", error);
            }
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: "image/*",
        onDrop,
    });

    const handleSaveUpdateClick = (event) => {
        event.preventDefault();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!name) {
            setNameError("Name cannot be empty");
        } else if (!email) {
            setEmailError("Email cannot be empty");
        } else if (name.length < 3) {
            setNameError("Name cannot be less than 3 characters");
        } else if (!emailRegex.test(email)) {
            setEmailError("Please enter a valid email address");
        } else {
            const body = {
                fullName: name,
                profilePhoto: binaryImage ? binaryImage : null,
                consentToDisplayWinnings: isCheck,
                emailAddress: email,
            };
            performSave(body);
        }
    };

    const performSave = async (body) => {
        const response = await createUpdateProfile(body);
        if (response && response.status === 200) {
            toast.success(response?.data?.message);
            setIsEdit(false);
            getUserProfile();
        }
    };

    const handleCheckChange = (event) => {
        setIsCheck(event.target.checked);
    };

    return (
        <Dialog
            open={profileHookState.get().isClicked === true ? true : false}
            onClose={() => profileHookState.set({ isClicked: false })}
            sx={{ "& .MuiDialog-paper": { borderRadius: "12px" } }}
        >
            <DialogContent>
                <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    width={{ xs: "100%", md: "100%" }}
                >
                    <Typography sx={styles.logoutTitleText}>User Profile</Typography>
                    <Box
                        onClick={() => profileHookState.set({ isClicked: false })}
                        sx={styles.closeIconStyle}
                    >
                        <IoClose size={24} color={theme.palette.primary.main} />
                    </Box>

                </Stack>
                <Divider flexItem sx={{ my: 1 }} />

                <Box sx={styles.contentContainer}>
                    <Box
                        sx={
                            isEdit
                                ? styles.editCancelButtonContainer
                                : styles.editButtonContainer
                        }
                        onClick={() => setIsEdit((prev) => !prev)}
                    >
                        <Typography
                            component={"label"}
                            sx={{
                                ...styles.editButtonText,
                                color: isEdit ? theme.custom.red : theme.custom.emperor,
                            }}
                        >
                            {isEdit ? "Cancel" : "Edit"}
                        </Typography>
                    </Box>
                    <Box sx={{ position: "relative" }}>
                        {isEdit && (
                            <Box sx={styles.editIconContainer}>
                                <FaPencilAlt size={14} color={theme.custom.black} />
                            </Box>
                        )}

                        {isEdit ? (
                            <Box sx={styles.profileImageContainer} {...getRootProps()}>
                                {imageLoader ? (
                                    <CircularProgress />
                                ) : (
                                    <>
                                        <input
                                            {...getInputProps()}
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: "100%",
                                                height: "100%",
                                                opacity: 0, // Make the input invisible
                                                cursor: "pointer",
                                                zIndex: 10, // Ensure pointer cursor
                                            }}
                                        />
                                        {binaryImage ? (
                                            <Box
                                                component={"img"}
                                                src={binaryImage}
                                                sx={styles.imageStyle}
                                            />
                                        ) : (
                                            <IoIosImages
                                                color={theme.palette.primary.main}
                                                size={40}
                                            />
                                        )}
                                    </>
                                )}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    ...styles.profileImageContainer,
                                    "&:hover": { cursor: "default" },
                                }}
                            >
                                {binaryImage ? (
                                    <Box
                                        component={"img"}
                                        src={binaryImage ? binaryImage : Logo}
                                        sx={styles.imageStyle}
                                    />
                                ) : (
                                    <IoIosImages color={theme.palette.primary.main} size={40} />
                                )}
                            </Box>
                        )}
                    </Box>

                    <Box>
                        <Typography
                            component={"label"}
                            sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: theme.custom.emperor,
                            }}
                        >
                            Enter your name
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            placeholder="Enter your name"
                            variant="outlined"
                            size="small"
                            disabled={!isEdit}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            error={!!nameError}
                            helperText={nameError || ""}
                            slotProps={{
                                input: { style: { fontSize: "14px", fontWeight: "500" } },
                            }}
                            sx={{ mb: 1 }}
                        />
                        <Typography
                            component={"label"}
                            sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: theme.custom.emperor,
                            }}
                        >
                            Enter your email address
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            placeholder="Enter your email address"
                            size="small"
                            disabled={!isEdit}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            error={!!emailError}
                            helperText={emailError || ""}
                            slotProps={{
                                input: { style: { fontSize: "14px", fontWeight: "500" } },
                            }}
                            sx={{ mb: 1 }}
                        />
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.custom.emperor,
                                fontWeight: 500,
                                mb: 1,
                                mt: 1.5,
                            }}
                        >Get Winning Notification on Email</Typography>
                        <Stack direction={"row"} alignItems={"flex-end"} ml={-1.5} mb={2}>
                            <Checkbox
                                label="Label 111"
                                onChange={handleCheckChange}
                                checked={isCheck}
                            />
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: theme.custom.emperor,
                                    fontWeight: 500,
                                    "& span": {
                                        color: theme.custom.adornment,
                                    },
                                }}
                            >
                                I give my consent for the public disclosure of my name, photo,
                                and winning details <span>(optional)</span>.
                            </Typography>
                        </Stack>
                                
            <Box sx={styles.backButtonContainer}>
                <CustomButton
                    title={buttonTitle}

                    onClick={handleSaveUpdateClick}
                />
            </Box>
                        {/* <CustomButton
                            title={buttonTitle}
                            onClick={handleSaveUpdateClick}
                        /> */}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ProfileDialog;

const styles = {
    logoutTitleText: {
        fontSize: "clamp(0.8rem, 3vw, 1rem)",
        fontWeight: 600,
        color: theme.palette.secondary.main,
    },
    closeIconStyle: {
        width: "1.1em",
        minWidth: "1.1em",
        maxWidth: "1.1em",
        aspectRatio: "1/1",
        cursor: "pointer",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
            transform: "scale(1.2)",
        },
    },
    profileImageContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mt: 2,
        mr: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderRadius: "50%",
        width: 80,
        height: 80,
        aspectRatio: "1/1",
        border: `1px solid ${theme.palette.primary.main}`,
        "&:hover": {
            cursor: "pointer",
        },
        position: "relative",
    },
    contentContainer: {
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "flex-start",
        position: "relative",
    },
    editButtonContainer: {
        position: "absolute",
        top: 0,
        right: 0,
        border: `1px solid ${theme.custom.emperor}`,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        "&:hover": {
            cursor: "pointer",
            backgroundColor: theme.custom.emperor,
        },
    },
    editCancelButtonContainer: {
        position: "absolute",
        top: 0,
        right: 0,
        border: `1px solid ${theme.custom.red}`,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        "&:hover": {
            cursor: "pointer",
            backgroundColor: theme.custom.red,
        },
    },
    editButtonText: {
        fontSize: 12,
        fontWeight: 400,
        py: 0.5,
        minWidth: 60,
        textAlign: "center",
        color: theme.custom.emperor,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
            cursor: "pointer",
            color: theme.custom.white,
        },
    },
    imageErrorStyle: {
        fontSize: "clamp(0.8rem, 2vw, 0.6rem)",
        fontWeight: 400,
        color: theme.custom.red,
    },
    editIconContainer: {
        position: "absolute",
        top: -20,
        right: -20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        zIndex: 1,
    },
    imageStyle: {
        width: 70,
        height: 70,
        objectFit: "cover",
        borderRadius: "50%",
    },
    backButtonContainer: {
        display: "flex",
        justifyContent: "flex-start",
        marginTop: "20px",
    },
};
